import { computed, useHost } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useUidAttribute } from "composables/useUidAttribute";
import { useHostAttribute } from "composables/useHostAttribute";
import { createEventDispatcher } from "@/runtime/shared/events";
import { parseBoolAttr } from "shared/attrs";
import { useTable } from "./useTable";
import { tableRowVariants } from "./Table.styles";
import type { TableRowProps } from "./TableRowProps";

export interface UseTableRowOptions {
  props: Readonly<TableRowProps>;
}

export const useTableRow = (options: UseTableRowOptions) => {
  const { props } = options;
  const host = useHost();
  const dispatchEvent = createEventDispatcher(host);

  useSharedState(props);
  useUidAttribute();

  const {
    syncRowSelection,
    handleRowClick,
    isRowSelected,
    isRowSelectable,
    isEvenRow,
    isTableStriped,
    isTableHoverable,
  } = useTable();

  syncRowSelection();

  const handleClick = () => {
    const result = handleRowClick();
    if (!result) return;
    dispatchEvent("update:selected", result.selected);
    dispatchEvent("click", result.rowIndex);
  };

  const stripeVariant = computed(() => {
    if (!isTableStriped.value) return "none";
    return isEvenRow.value ? "even" : "odd";
  });

  const classes = computed(() =>
    tableRowVariants({
      disabled: parseBoolAttr(props.disabled, false),
      stripe: stripeVariant.value,
      hoverable: isTableHoverable.value,
      selected: isRowSelected.value,
    }),
  );

  useHostAttribute("selected", isRowSelected);

  return { classes, isRowSelected, isRowSelectable, handleClick };
};
